import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const effectiveDate = "March 18, 2026";
  const companyName = "TerryLBPrompts";
  const websiteUrl = "terrylbprompts.com";
  const contactEmail = "support@terrylbprompts.com";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-500 mb-8">Effective Date: {effectiveDate}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
            <p>
              Welcome to {companyName} ("we," "our," or "us"). We are committed to protecting your privacy 
              and personal information. This Privacy Policy explains how we collect, use, disclose, and 
              safeguard your information when you use our website at {websiteUrl}, our mobile applications, 
              and our services including DailyDrop AI (collectively, the "Service").
            </p>
            <p className="mt-2">
              By using our Service, you agree to the collection and use of information in accordance with 
              this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mt-4 mb-2">2.1 Information You Provide</h3>
            <ul className="list-disc pl-6">
              <li><strong>Account Information:</strong> Name, email address, profile picture (via Google OAuth)</li>
              <li><strong>Profile Data:</strong> Content niche, platform preferences, posting goals</li>
              <li><strong>Payment Information:</strong> Processed securely through Stripe; we do not store credit card numbers</li>
              <li><strong>User Content:</strong> Prompts, captions, images, and other content you create</li>
              <li><strong>Communications:</strong> Messages you send to us for support</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4 mb-2">2.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6">
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on Service</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, referring URLs</li>
              <li><strong>Cookies:</strong> Session cookies for authentication and preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mt-4 mb-2">2.3 Third-Party Platform Data</h3>
            <p>
              When you connect social media accounts (Facebook, Instagram, TikTok, YouTube, Twitter), we may access:
            </p>
            <ul className="list-disc pl-6">
              <li>Basic profile information (name, username, profile picture)</li>
              <li>Permissions to post content on your behalf</li>
              <li>Analytics data about your posts (with your consent)</li>
            </ul>
            <p className="mt-2">
              We only request permissions necessary for the Service to function. You can revoke access at any time.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Generate AI-powered content tailored to your preferences</li>
              <li>Post content to your connected social media accounts</li>
              <li>Send notifications about your account, streaks, and updates</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage trends to improve user experience</li>
              <li>Detect, prevent, and address technical issues or fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Information Sharing</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 mt-2">
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
              <li><strong>Service Providers:</strong> Third parties who assist in operating our Service (hosting, payment processing, analytics)</li>
              <li><strong>Social Media Platforms:</strong> When you use DailyDrop AI to post content</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="mt-2">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to 
              provide you services. We will retain and use your information as necessary to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Comply with our legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce our agreements</li>
            </ul>
            <p className="mt-2">
              You can request deletion of your account and associated data by contacting us at {contactEmail}.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These include:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure authentication via OAuth 2.0</li>
              <li>Regular security assessments</li>
              <li>Access controls and authentication for our systems</li>
            </ul>
            <p className="mt-2">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee 
              absolute security of your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. Your Rights and Choices</h2>
            <p>Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 mt-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Withdraw Consent:</strong> Revoke previously given consent</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at {contactEmail}.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Cookies and Tracking</h2>
            <p>
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Keep you signed in</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our Service</li>
              <li>Improve our Service</li>
            </ul>
            <p className="mt-2">
              You can control cookies through your browser settings. Note that disabling cookies may 
              affect Service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Third-Party Services</h2>
            <p>Our Service integrates with third-party services including:</p>
            <ul className="list-disc pl-6 mt-2">
              <li><strong>Google:</strong> Authentication (OAuth) and AI services</li>
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Social Media Platforms:</strong> Facebook, Instagram, TikTok, YouTube, Twitter</li>
            </ul>
            <p className="mt-2">
              These services have their own privacy policies. We encourage you to review their policies 
              before using their services through our platform.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If we discover that a child under 13 has provided 
              us with personal information, we will delete it immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country 
              of residence. These countries may have different data protection laws. We take appropriate 
              safeguards to ensure your information remains protected.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">12. California Privacy Rights (CCPA)</h2>
            <p>
              If you are a California resident, you have additional rights under the California Consumer 
              Privacy Act (CCPA), including the right to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Know what personal information we collect</li>
              <li>Request deletion of your personal information</li>
              <li>Opt-out of the sale of personal information (we do not sell your data)</li>
              <li>Non-discrimination for exercising your rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by 
              posting the new Privacy Policy on this page and updating the "Effective Date." We encourage 
              you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">14. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> {contactEmail}<br />
              <strong>Website:</strong> {websiteUrl}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© 2026 {companyName}. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-gray-700">Privacy Policy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-gray-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
